import {
  Dispatch,
  memo,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import Loader from './Loader'
import { SvgUndo, SvgZoomIn, SvgZoomOut, SvgRedo } from '../assets/images'
import 'pdfjs-dist/web/pdf_viewer.css'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import workerSrc from 'pdfjs-dist/build/pdf.worker?url'
import { PDFDocumentProxy } from 'pdfjs-dist'

pdfjs.GlobalWorkerOptions.workerSrc = workerSrc

const getPdfFileSource = (base64String: string): string => {
  return `data:application/pdf;base64,${base64String}`
}

interface PdfViewerPropsType {
  pdfUrl: string
  isOutline?: boolean
  width?: string
  height?: string
  showLoader?: boolean
  isBase64?: boolean
  allowZoom?: boolean
  showPageNumber?: boolean
  defaultZoomScale?: number
  setTotalPages?: Dispatch<SetStateAction<number>>
}

// Might need later
// pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const options = {
  cMapUrl: '/cmaps/',
  standardFontDataUrl: '/standard_fonts/',
}

const PdfViewer = ({
  pdfUrl,
  isOutline = false,
  width = 'w-full',
  height = 'h-fit',
  showLoader,
  isBase64,
  allowZoom = true,
  showPageNumber = true,
  defaultZoomScale = 1,
  setTotalPages,
}: PdfViewerPropsType) => {
  const [numPages, setNumPages] = useState<number>(0)
  const pageRefs = useRef<(HTMLDivElement | null)[]>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const containerRef = useRef<HTMLDivElement>(null)

  const [scale, setScale] = useState<number>(defaultZoomScale ?? 1)
  const [rotation, setRotation] = useState<number>(0) // Initial rotation angle
  const rotateLeft = () => setRotation((prev) => (prev - 90) % 360)
  const rotateRight = () => setRotation((prev) => (prev + 90) % 360)
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null)
  const zoomIn = () => setScale(Math.min(scale + 0.2, 3)) // Max 300%
  const zoomOut = () => setScale(Math.max(scale - 0.2, 0.5)) // Min 50%

  const onDocumentLoadSuccess = (pdf: PDFDocumentProxy) => {
    setNumPages(pdf.numPages)
    setTotalPages && setTotalPages(pdf.numPages)
    pageRefs.current = Array(pdf.numPages).fill(null)
  }

  const handleScroll = () => {
    const containerTop = containerRef.current?.getBoundingClientRect().top ?? 0

    let visiblePage = pageRefs.current.length ? 1 : 0
    for (let i = 0; i < pageRefs.current.length; i++) {
      const page = pageRefs.current[i]
      if (page) {
        const { top } = page.getBoundingClientRect()
        if (top - containerTop < 300) {
          visiblePage = i + 1
        }
      }
    }

    setCurrentPage(visiblePage)
  }

  const handleZoomChange = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const value = parseInt(event.currentTarget.value, 10)
    if (!isNaN(value) && value >= 50 && value <= 300) {
      setScale(value / 100)
    }
  }

  useEffect(() => {
    setCurrentPage(pageRefs.current.length ? 1 : 0)
  }, [pageRefs.current.length])

  useEffect(() => {
    let isCancelled = false

    const loadPdf = async () => {
      try {
        if (isBase64) {
          setPdfBlobUrl(pdfUrl)
          return
        }

        setPdfBlobUrl(null)
        const res = await fetch(pdfUrl?.replace(/\s+/g, ''), {
          cache: 'reload',
        })

        if (!res.ok) throw new Error(`Failed to fetch PDF: ${res.status}`)

        const blob = await res.blob()
        const blobUrl = URL.createObjectURL(blob)

        setTimeout(() => {
          if (!isCancelled) {
            setPdfBlobUrl(blobUrl)
          }
        }, 50)
      } catch (err) {
        console.error('Failed to fetch PDF blob:', err)
      }
    }

    loadPdf()

    return () => {
      isCancelled = true
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl)
      }
    }
  }, [pdfUrl, isBase64])

  return (
    <>
      <div
        className={`action-btns flex justify-center p-1 gap-2 absolute w-full top-0 pt-[0.35rem] left-0 z-[1] ${
          !allowZoom ? 'hidden' : ''
        }`}
      >
        <span className="border inline-flex align-center border-color-[#E5E5E5] rounded-[0.25rem] bg-accent-0">
          <button
            type="button"
            color="primary"
            className="w-[1.55rem] border-accent-110"
            onClick={() => zoomOut()}
            data-qa-id="rotate-pdf-right"
          >
            <SvgZoomOut />
          </button>

          <input
            className="w-10 h-[1.5rem] text-[0.75rem] text-accent-600 pointer-events-none"
            type="text"
            value={`${Math.round(scale * 100)}%`}
            onKeyDown={(event) => handleZoomChange(event)}
            min={50}
            max={300}
          />
          <button
            type="button"
            color="primary"
            className="w-[1.55rem] border-accent-110"
            onClick={() => zoomIn()}
            data-qa-id="rotate-pdf-left"
          >
            <SvgZoomIn />
          </button>
          <button
            type="button"
            color="primary"
            className="w-[1.55rem] border-accent-110"
            onClick={() => rotateLeft()}
            data-qa-id="rotate-pdf-left"
          >
            <SvgUndo />
          </button>
          <button
            type="button"
            className="w-[1.55rem] border-accent-110"
            color="primary"
            onClick={() => rotateRight()}
            data-qa-id="rotate-pdf-right"
          >
            <SvgRedo />
          </button>
        </span>
      </div>

      <div
        className={`p-1 relative h-full py-0 bg-white overflow-y-auto pdf-wrap-box`}
        ref={containerRef}
        onScroll={handleScroll}
      >
        <div className={`${width} ${height} h-[56vh]`}>
          {pdfBlobUrl ? (
            <Document
              file={isBase64 ? getPdfFileSource(pdfBlobUrl) : pdfBlobUrl}
              options={options}
              className={`${isOutline ? 'border-2 border-black' : ''}`}
              loading={
                showLoader ? (
                  <div className="min-h-[40rem] min-w-[40rem] w-full flex items-center justify-center flex-1">
                    <Loader
                      isGlobal={false}
                      loaderHeight="3rem"
                      loaderWidth="3rem"
                    />
                  </div>
                ) : null
              }
              error={null}
              onLoadSuccess={onDocumentLoadSuccess}
            >
              {Array.from(new Array(numPages), (el, index) => (
                <div
                  className="flex justify-start items-start mb-4"
                  key={`page_${index + 1}`}
                  ref={(el) => (pageRefs.current[index] = el)}
                >
                  {pdfBlobUrl && numPages > 0 && (
                    <Page
                      rotate={rotation}
                      pageNumber={index + 1}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      scale={scale}
                      className="text-left"
                    />
                  )}
                </div>
              ))}
            </Document>
          ) : null}
        </div>
      </div>
      <span
        className={`page-count text-white align-top max-w-20 text-xs font-normal text-center leading-[2] absolute bottom-4 left-0 right-0 mx-auto rounded-md bg-black/60 ${
          !showPageNumber ? '!hidden' : ''
        }`}
      >
        {currentPage}/{numPages}
      </span>
    </>
  )
}

export default memo(PdfViewer)
